import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Check, Upload, ArrowRight, FileText, Shield, Clock, UserCheck, CheckCircle } from 'lucide-react';
import { API } from '../App';
import { toast } from 'sonner';
import TurnstileWidget from '../components/TurnstileWidget';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ALLOWED_TYPES = ['.pdf', '.doc', '.docx'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export default function StartPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    preferred_contact: 'email',
    target_role: '',
    industry: '',
    services_needed: [],
    turnaround_urgency: 'standard',
    linkedin_url: '',
    needs_cover_letter: false,
    notes: ''
  });

  const serviceOptions = [
    { id: 'resume_rewrite', label: 'Resume Rewrite', desc: 'Complete ATS-friendly resume transformation' },
    { id: 'resume_cleanup', label: 'Resume Cleanup', desc: 'Light formatting and grammar improvements' },
    { id: 'linkedin', label: 'LinkedIn Optimization', desc: 'Headline + About section' },
    { id: 'apply_assist', label: 'Application Support', desc: 'Structured help with job applications' },
  ];

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services_needed: prev.services_needed.includes(serviceId)
        ? prev.services_needed.filter(s => s !== serviceId)
        : [...prev.services_needed, serviceId]
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error('File too large. Maximum size is 10MB.');
        return;
      }
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      if (!ALLOWED_TYPES.includes(ext)) {
        toast.error('Please upload a PDF, DOC, or DOCX file.');
        return;
      }
      setResumeFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.full_name.trim()) {
      toast.error('Please provide your name.');
      return;
    }
    if (!formData.email.trim() || !EMAIL_RE.test(formData.email.trim())) {
      toast.error('Please provide a valid email address.');
      return;
    }
    if (!resumeFile) {
      toast.error('Please upload your current resume.');
      return;
    }
    if (formData.services_needed.length === 0) {
      toast.error('Please select at least one service.');
      return;
    }
    if (!turnstileToken) {
      toast.error('Please complete the security check.');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('resume', resumeFile);
      submitData.append('full_name', formData.full_name.trim());
      submitData.append('email', formData.email.trim());
      submitData.append('phone', formData.phone.trim());
      submitData.append('preferred_contact', formData.preferred_contact);
      submitData.append('target_role', formData.target_role.trim());
      submitData.append('industry', formData.industry.trim());
      submitData.append('services_needed', JSON.stringify(formData.services_needed));
      submitData.append('turnaround_urgency', formData.turnaround_urgency);
      submitData.append('linkedin_url', formData.linkedin_url.trim());
      submitData.append('needs_cover_letter', formData.needs_cover_letter.toString());
      submitData.append('notes', formData.notes.trim());
      submitData.append('turnstile_token', turnstileToken);

      const response = await fetch(`${API}/intake`, {
        method: 'POST',
        body: submitData,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Submission failed');
      }

      setSubmitted(true);
      toast.success("Intake submitted! We'll review your materials and be in touch.");
    } catch (error) {
      console.error('Intake submission error:', error);
      toast.error(error.message || 'Failed to submit intake. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="pt-24 pb-20">
          <div className="max-w-2xl mx-auto px-6">
            <Card className="p-8 md:p-12 rounded-3xl text-center" data-testid="intake-success">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-teal-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
                Thanks, {formData.full_name.split(' ')[0]}!
              </h1>
              <p className="text-slate-600 text-lg mb-2">
                Your intake has been received.
              </p>
              <p className="text-slate-500 mb-8">
                We'll review your resume and reach out via your preferred contact method with next steps. Expect to hear from us within 24 hours.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => navigate('/')}
                  className="bg-teal-600 hover:bg-teal-700 rounded-full py-6"
                  data-testid="intake-success-home"
                >
                  Back to Home
                </Button>
              </div>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge className="bg-teal-100 text-teal-700 mb-4">Step 1 of the Process</Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              Start Your Intake
            </h1>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Tell us about your resume needs. We'll review your materials personally and confirm the right scope before sending an invoice.
            </p>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 mb-12">
            <div className="flex items-center gap-2 text-slate-600">
              <UserCheck className="h-5 w-5 text-teal-600" />
              <span className="text-sm">Reviewed before billing</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Shield className="h-5 w-5 text-teal-600" />
              <span className="text-sm">Scope confirmed first</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Clock className="h-5 w-5 text-teal-600" />
              <span className="text-sm">Usually responds within 24h</span>
            </div>
          </div>

          {/* Intake Form */}
          <Card className="p-8 rounded-3xl border border-slate-200 bg-white">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Your Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="John Smith"
                      required
                      data-testid="intake-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      required
                      data-testid="intake-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      data-testid="intake-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="preferred_contact">Preferred Contact Method</Label>
                    <Select 
                      value={formData.preferred_contact} 
                      onValueChange={(value) => setFormData({ ...formData, preferred_contact: value })}
                    >
                      <SelectTrigger data-testid="intake-preferred-contact">
                        <SelectValue placeholder="How should we reach you?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone Call</SelectItem>
                        <SelectItem value="text">Text Message</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="linkedin_url">LinkedIn URL (optional)</Label>
                    <Input
                      id="linkedin_url"
                      value={formData.linkedin_url}
                      onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                      placeholder="linkedin.com/in/yourprofile"
                      data-testid="intake-linkedin"
                    />
                  </div>
                </div>
              </div>

              {/* Resume Upload */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Your Current Resume *</h3>
                <div 
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                    resumeFile ? 'border-teal-500 bg-teal-50' : 'border-slate-300 hover:border-slate-400'
                  }`}
                >
                  <input
                    type="file"
                    id="resume"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="intake-resume-upload"
                  />
                  <label htmlFor="resume" className="cursor-pointer">
                    {resumeFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <FileText className="h-8 w-8 text-teal-600" />
                        <div className="text-left">
                          <p className="font-medium text-slate-900">{resumeFile.name}</p>
                          <p className="text-sm text-slate-500">{(resumeFile.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={(e) => { e.preventDefault(); setResumeFile(null); }}>
                          Change
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                        <p className="text-slate-600 mb-2">Click to upload your resume</p>
                        <p className="text-sm text-slate-400">PDF, DOC, or DOCX (max 10MB)</p>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Target Role */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Target Role & Industry</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="target_role">Target Job Title / Role</Label>
                    <Input
                      id="target_role"
                      value={formData.target_role}
                      onChange={(e) => setFormData({ ...formData, target_role: e.target.value })}
                      placeholder="e.g., Senior Marketing Manager"
                      data-testid="intake-target-role"
                    />
                  </div>
                  <div>
                    <Label htmlFor="industry">Industry</Label>
                    <Input
                      id="industry"
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      placeholder="e.g., Technology, Healthcare, Finance"
                      data-testid="intake-industry"
                    />
                  </div>
                </div>
              </div>

              {/* Services Needed */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Services Needed *</h3>
                <p className="text-sm text-slate-500 mb-4">Select all that apply. We'll confirm the exact scope after reviewing your materials.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {serviceOptions.map((service) => (
                    <div
                      key={service.id}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.services_needed.includes(service.id)
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleServiceToggle(service.id)}
                      data-testid={`service-option-${service.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                          formData.services_needed.includes(service.id)
                            ? 'border-teal-500 bg-teal-500'
                            : 'border-slate-300'
                        }`}>
                          {formData.services_needed.includes(service.id) && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{service.label}</p>
                          <p className="text-sm text-slate-500">{service.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Urgency */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Turnaround Urgency</h3>
                <Select 
                  value={formData.turnaround_urgency} 
                  onValueChange={(value) => setFormData({ ...formData, turnaround_urgency: value })}
                >
                  <SelectTrigger data-testid="intake-urgency">
                    <SelectValue placeholder="Select urgency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard (3-5 business days)</SelectItem>
                    <SelectItem value="priority">Priority (24-48 hours)</SelectItem>
                    <SelectItem value="rush">Rush (same day if possible)</SelectItem>
                    <SelectItem value="flexible">Flexible / No rush</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Cover Letter */}
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="needs_cover_letter"
                  checked={formData.needs_cover_letter}
                  onCheckedChange={(checked) => setFormData({ ...formData, needs_cover_letter: checked })}
                  data-testid="intake-cover-letter"
                />
                <Label htmlFor="needs_cover_letter" className="cursor-pointer">
                  I also need help with a cover letter
                </Label>
              </div>

              {/* Additional Notes */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Additional Notes</h3>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Tell us anything else that might help us understand your needs. Career goals, specific challenges, companies you're targeting, etc."
                  rows={4}
                  data-testid="intake-notes"
                />
              </div>

              {/* Spam Protection */}
              <div>
                <TurnstileWidget onVerify={setTurnstileToken} />
              </div>

              {/* Submit */}
              <div className="pt-4">
                <Button 
                  type="submit"
                  className="w-full bg-teal-600 hover:bg-teal-700 rounded-full py-6 text-lg font-semibold"
                  disabled={loading || !turnstileToken}
                  data-testid="intake-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                      Submitting...
                    </span>
                  ) : (
                    <>
                      Submit Intake
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
                <p className="text-center text-sm text-slate-500 mt-4">
                  By submitting, you agree to our terms of service. You won't be charged until we review your materials and confirm the scope.
                </p>
              </div>
            </form>
          </Card>

          {/* What Happens Next */}
          <Card className="mt-8 p-6 rounded-2xl bg-slate-100 border-0">
            <h4 className="font-semibold text-slate-900 mb-4">What happens after you submit?</h4>
            <ol className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                <span>We review your resume and intake information personally.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                <span>We'll reach out via your preferred contact method if we need any clarification.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                <span>Once the scope is confirmed, we send your Stripe invoice.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                <span>Work begins after payment is received.</span>
              </li>
            </ol>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
